import { loadThresholds, type Thresholds } from '../../../core/thresholds';
import { COLORS } from '../../IndividualPlan/tokens';
import { CohortBaselinePanel } from '../components/CohortBaselinePanel';
import { CohortCard } from '../components/CohortCard';
import { type Finding } from '../components/FindingsList';
import { NotableFindings } from '../components/NotableFindings';
import { SectionHeading } from '../program-evaluation.styled';
import type {
  PeCohortBaseline,
  PeCohortDetail,
  PeCohortOrAbsent,
  PeSection,
  PeSubgroups,
  ScoreUnit,
} from '../types';
import { parsePatterns } from '../utils/parsePatterns';
import { decodeEntities } from '../utils/parseFindings';
import { splitFindingText } from '../utils/splitFindingText';

type SubgroupFindingsProps = {
  subgroups: PeSubgroups;
  sections: PeSection[];
  unit: ScoreUnit;
  notablePatternsText: string;
  residualPatterns: boolean;
  thresholds?: Thresholds;
  // Near-exit and Alternate ACCESS are defined by scores; without any,
  // nobody is in either cohort and the panels are withheld.
  assessmentDataAbsent?: boolean;
  cohorts?: PeCohortDetail[];
  schoolYear?: string;
  lastYearKnown?: boolean;
  threshold?: number;
};

const COHORT_ORDER = ['ltel', 'newcomer', 'review', 'dual'];

const RESIDUAL_LINE = 'Additional subgroup patterns are reflected in the cohort baselines above';

const SECTION_DESCRIPTION =
  'Cohort baselines for the four populations a director is directly accountable for, plus notable patterns surfaced across all subgroup dimensions. Names the pattern; does not claim cause.';

const DESCRIPTION_STYLE = {
  margin: '4px 0 0',
  maxWidth: 720,
  fontSize: 16,
  lineHeight: '24px',
  color: COLORS.muted,
};

const SUBHEADING_STYLE = {
  margin: '20px 0 0',
  fontSize: 16,
  fontWeight: 700,
  color: COLORS.muted,
};

const COHORT_GRID_STYLE = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 16,
  alignItems: 'stretch',
  marginTop: 8,
};

const NOTABLE_PATTERNS_NOTE =
  'Subgroups whose metrics diverge meaningfully from district average or own prior year.';

function cohortQualifiers(thresholds: Thresholds): Record<string, string> {
  return {
    ltel: `≥${thresholds.ltelMinYears} yrs in program`,
    newcomer: `≤${thresholds.newcomerMaxYears} yrs in program`,
    near_reclassification: 'composite met • domain unmet',
    dually_identified: 'EL + SPED',
  };
}

function unwrap(text: string, tag: string): string {
  const re = new RegExp(`^\\s*<${tag}\\b[^>]*>([\\s\\S]*?)</${tag}>\\s*$`, 'i');
  const match = text.match(re);
  return decodeEntities((match?.[1] ?? text).trim());
}

function findSection(
  sections: PeSection[],
  element: string,
  cohortKey?: string,
): PeSection | undefined {
  return sections.find(
    (s) => s.element === element && (cohortKey === undefined || s.cohort_key === cohortKey),
  );
}

function framingFor(sections: PeSection[], cohortKey: string): string {
  const section = findSection(sections, 'cohort_baseline', cohortKey);
  return section ? unwrap(section.text, 'narrative') : '';
}

function isBaseline(cohort: PeCohortOrAbsent): cohort is PeCohortBaseline {
  return (
    cohort.absent !== true &&
    typeof cohort.cohort_size === 'number' &&
    typeof cohort.percent_of_program === 'number' &&
    'year_over_year_change' in cohort &&
    cohort.growth !== undefined &&
    cohort.reclassification_within_cohort !== undefined &&
    cohort.comparison_to_non_cohort !== undefined &&
    typeof cohort.small_n_caveat === 'boolean'
  );
}

function asBaseline(cohort: PeCohortOrAbsent): PeCohortBaseline | null {
  return isBaseline(cohort) ? cohort : null;
}

export function SubgroupFindings({
  subgroups,
  sections,
  unit,
  notablePatternsText,
  residualPatterns,
  thresholds = loadThresholds(),
  assessmentDataAbsent = false,
  cohorts,
  schoolYear = '',
  lastYearKnown = true,
  threshold = 10,
}: SubgroupFindingsProps) {
  const qualifiers = cohortQualifiers(thresholds);
  const criticalCohorts: Array<{
    key: string;
    label: string;
    baseline: PeCohortBaseline | null;
    absent: boolean;
  }> = [
    { key: 'ltel', label: 'LTEL', baseline: subgroups.ltel, absent: false },
    {
      key: 'newcomer',
      label: 'Newcomers',
      baseline: subgroups.newcomer,
      absent: false,
    },
    {
      key: 'near_reclassification',
      label: 'Near-exit',
      baseline: subgroups.near_reclassification,
      absent: assessmentDataAbsent,
    },
    {
      key: 'dually_identified',
      label: 'Dually identified',
      baseline: asBaseline(subgroups.dually_identified),
      absent: !!subgroups.dually_identified.absent,
    },
  ];

  const patternFindings: Finding[] = parsePatterns(notablePatternsText).map((pattern) => {
    const { headline, supporting } = splitFindingText(pattern);
    return { text: headline, subline: supporting };
  });

  return (
    <section className="subgroup-findings">
      <SectionHeading id="subgroup-findings-heading">Cohort Findings</SectionHeading>
      <p className="subgroup-findings-description" style={DESCRIPTION_STYLE}>
        {SECTION_DESCRIPTION}
      </p>

      <h3
        className="subgroup-findings-subheading"
        data-testid="subgroup-findings-subheading"
        style={SUBHEADING_STYLE}
      >
        Cohort Baselines
      </h3>

      <div
        className="subgroup-findings-grid"
        data-testid="cohort-baselines-grid"
        style={COHORT_GRID_STYLE}
      >
        {cohorts
          ? [...cohorts]
              .sort((a, b) => COHORT_ORDER.indexOf(a.cohort) - COHORT_ORDER.indexOf(b.cohort))
              .map((cohort) => (
                <CohortCard
                  key={cohort.cohort}
                  cohort={cohort}
                  threshold={threshold}
                  schoolYear={schoolYear}
                  lastYearKnown={lastYearKnown}
                />
              ))
          : criticalCohorts.map((cohort) => (
              <CohortBaselinePanel
                key={cohort.key}
                cohortLabel={cohort.label}
                qualifier={qualifiers[cohort.key]}
                baseline={cohort.baseline}
                absent={cohort.absent}
                framingText={framingFor(sections, cohort.key)}
                unit={unit}
              />
            ))}

        {(!cohorts || !subgroups.alternate_access.absent) && (
          <CohortBaselinePanel
            cohortLabel="Alternate ACCESS"
            baseline={asBaseline(subgroups.alternate_access)}
            absent={assessmentDataAbsent || !!subgroups.alternate_access.absent}
            framingText={framingFor(sections, 'alternate_access')}
            unit={unit}
          />
        )}
      </div>

      <NotableFindings
        title="Notable Findings"
        note={NOTABLE_PATTERNS_NOTE}
        findings={patternFindings}
        blockTestId="subgroup-notable-patterns"
      />

      {residualPatterns && <p className="subgroup-residual">{RESIDUAL_LINE}</p>}
    </section>
  );
}
